import { useState, useEffect } from "react";

// Renders one question card. Uses controlled inputs sourced from `answers`.
// Emits changes via `onChange(patch)` where patch is a partial answers object.
const QuestionCard = ({ q, answers, onChange, error }) => {
  const value = answers[q.id];

  // --- Text / email / number / date ---
  if (q.type === "text" || q.type === "date" || q.type === "number") {
    const inputType = q.type === "date" ? "date" : q.inputType || (q.type === "number" ? "number" : "text");
    return (
      <div>
        <Label q={q} />
        <div className="relative flex items-center">
          <input
            type={inputType}
            value={value ?? ""}
            onChange={(e) => onChange({ [q.id]: e.target.value })}
            placeholder={q.placeholder || ""}
            maxLength={q.maxLength}
            max={q.max === "today" ? new Date().toISOString().slice(0, 10) : q.max}
            min={q.min}
            pattern={q.pattern}
            data-testid={`ic-input-${q.id}`}
            className="w-full text-2xl md:text-3xl font-serif text-[#0B2343] bg-transparent border-0 border-b-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none py-3 placeholder:text-slate-300 transition-colors"
          />
          {q.suffix && (
            <span className="text-lg text-slate-400 ml-3 whitespace-nowrap">{q.suffix}</span>
          )}
        </div>
        <ErrorText error={error} />
      </div>
    );
  }

  // --- Textarea ---
  if (q.type === "textarea") {
    return (
      <div>
        <Label q={q} />
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange({ [q.id]: e.target.value })}
          placeholder={q.placeholder || ""}
          rows={5}
          data-testid={`ic-input-${q.id}`}
          className="w-full text-lg font-sans text-[#0B2343] bg-white border-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none p-4 placeholder:text-slate-300 transition-colors resize-none"
        />
        <ErrorText error={error} />
      </div>
    );
  }

  // --- Height (feet + inches) ---
  if (q.type === "height") {
    return (
      <div>
        <Label q={q} />
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <input
              type="number"
              min={0}
              max={9}
              value={answers.heightFeet ?? ""}
              onChange={(e) => onChange({ heightFeet: parseInt(e.target.value) || null })}
              placeholder="0"
              data-testid="ic-input-heightFeet"
              className="w-full text-2xl md:text-3xl font-serif text-[#0B2343] bg-transparent border-0 border-b-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none py-3 placeholder:text-slate-300"
            />
            <span className="text-sm text-slate-400 mt-1 block">feet</span>
          </div>
          <div className="flex-1">
            <input
              type="number"
              min={0}
              max={11}
              value={answers.heightInches ?? ""}
              onChange={(e) => onChange({ heightInches: parseInt(e.target.value) || 0 })}
              placeholder="0"
              data-testid="ic-input-heightInches"
              className="w-full text-2xl md:text-3xl font-serif text-[#0B2343] bg-transparent border-0 border-b-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none py-3 placeholder:text-slate-300"
            />
            <span className="text-sm text-slate-400 mt-1 block">inches</span>
          </div>
        </div>
        <ErrorText error={error} />
      </div>
    );
  }

  // --- Name group ---
  if (q.type === "nameGroup") {
    return (
      <div>
        <Label q={q} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NameField label="First Name *" id="firstName" value={answers.firstName ?? ""} onChange={onChange} />
          <NameField label="Middle Name" id="middleName" value={answers.middleName ?? ""} onChange={onChange} optional />
          <NameField label="Last Name *" id="lastName" value={answers.lastName ?? ""} onChange={onChange} />
          <NameField label="Suffix" id="suffix" value={answers.suffix ?? ""} onChange={onChange} optional />
        </div>
        <ErrorText error={error} />
      </div>
    );
  }

  // --- Radio (single choice) ---
  if (q.type === "radio") {
    return (
      <div>
        <Label q={q} />
        <div className="space-y-2">
          {q.options.map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange({ [q.id]: opt })}
                data-testid={`ic-opt-${q.id}-${slug(opt)}`}
                className={`w-full text-left px-5 py-4 border-2 transition-all ${
                  selected
                    ? "border-[#C89B3C] bg-[#C89B3C]/10 text-[#0B2343]"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <span className={`inline-block w-4 h-4 border-2 mr-3 rounded-full align-middle ${
                  selected ? "border-[#C89B3C] bg-[#C89B3C]" : "border-slate-300"
                }`} />
                <span className="align-middle text-base">{opt}</span>
              </button>
            );
          })}
        </div>
        <FollowUpText q={q} answers={answers} onChange={onChange} />
        <ErrorText error={error} />
      </div>
    );
  }

  // --- Multi-select (checkbox list, with optional exclusive option) ---
  if (q.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      let next;
      if (q.exclusiveOption && opt === q.exclusiveOption) {
        next = arr.includes(opt) ? [] : [opt];
      } else {
        next = arr.filter((v) => v !== q.exclusiveOption);
        next = next.includes(opt) ? next.filter((v) => v !== opt) : [...next, opt];
      }
      onChange({ [q.id]: next });
    };
    return (
      <div>
        <Label q={q} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {q.options.map((opt) => {
            const selected = arr.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                data-testid={`ic-opt-${q.id}-${slug(opt)}`}
                className={`text-left px-4 py-3 border-2 transition-all ${
                  selected
                    ? "border-[#C89B3C] bg-[#C89B3C]/10 text-[#0B2343]"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <span className={`inline-block w-4 h-4 border-2 mr-3 align-middle ${
                  selected ? "border-[#C89B3C] bg-[#C89B3C]" : "border-slate-300"
                }`} />
                <span className="align-middle text-sm">{opt}</span>
              </button>
            );
          })}
        </div>
        <FollowUpText q={q} answers={answers} onChange={onChange} />
        <FollowUpAltText q={q} answers={answers} onChange={onChange} />
        <ErrorText error={error} />
      </div>
    );
  }

  return null;
};

// ---- helpers ----
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

const Label = ({ q }) => (
  <>
    <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0B2343] leading-tight mb-3">
      {q.title}
      {q.required && <span className="text-[#C89B3C] ml-1">*</span>}
    </h2>
    {q.supportingText && (
      <p className="text-sm text-slate-500 mb-5 leading-relaxed">{q.supportingText}</p>
    )}
  </>
);

const NameField = ({ id, label, value, onChange, optional }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">
      {label} {optional && <span className="text-slate-400 normal-case">(optional)</span>}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange({ [id]: e.target.value })}
      data-testid={`ic-input-${id}`}
      className="w-full text-lg font-sans text-[#0B2343] bg-transparent border-0 border-b-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none py-2"
    />
  </div>
);

const FollowUpText = ({ q, answers, onChange }) => {
  const fu = q.followUp;
  if (!fu || !fu.when(answers)) return null;
  return (
    <div className="mt-4">
      <input
        type="text"
        value={answers[fu.field] ?? ""}
        onChange={(e) => onChange({ [fu.field]: e.target.value })}
        placeholder={fu.placeholder || ""}
        data-testid={`ic-input-${fu.field}`}
        className="w-full text-base text-[#0B2343] bg-transparent border-0 border-b-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none py-2"
      />
    </div>
  );
};

const FollowUpAltText = ({ q, answers, onChange }) => {
  const fu = q.followUpAlt;
  if (!fu || !fu.when(answers)) return null;
  return (
    <div className="mt-4">
      <input
        type="text"
        value={answers[fu.field] ?? ""}
        onChange={(e) => onChange({ [fu.field]: e.target.value })}
        placeholder={fu.placeholder || ""}
        data-testid={`ic-input-${fu.field}`}
        className="w-full text-base text-[#0B2343] bg-transparent border-0 border-b-2 border-slate-200 focus:border-[#C89B3C] focus:outline-none py-2"
      />
    </div>
  );
};

const ErrorText = ({ error }) => {
  if (!error) return null;
  return <p className="text-sm text-red-600 mt-3">{error}</p>;
};

export default QuestionCard;
